import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Support both 'token' (full) and 't' (short URL) parameters
  const token = searchParams.get('token') || searchParams.get('t');

  const [tokenStatus, setTokenStatus] = useState('verifying'); // verifying, valid, invalid, expired, used
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-reset-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenStatus('valid');
      } else {
        setTokenStatus(data.reason || 'invalid');
      }
    } catch (err) {
      setTokenStatus('invalid');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.newPassword) {
      setError('Yeni şifre gereklidir');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Şifre değiştirme başarısız oldu');
      }

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err.message || 'Şifre değiştirme başarısız oldu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while verifying token
  if (tokenStatus === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 text-center">
          <div className="mx-auto flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Veribil Logo" className="h-24 w-auto" />
          </div>
          <div className="flex flex-col items-center">
            <Loader className="h-8 w-8 text-[#7a2c2c] animate-spin mb-4" />
            <p className="text-gray-600">Doğrulanıyor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid/expired/used
  if (tokenStatus !== 'valid' && tokenStatus !== 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center mb-8">
              <img src="/logo.png" alt="Veribil Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {tokenStatus === 'expired' && 'Linkin Süresi Dolmuş'}
              {tokenStatus === 'used' && 'Link Kullanılmış'}
              {(tokenStatus === 'invalid' || tokenStatus === 'not_found') && 'Geçersiz Link'}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {tokenStatus === 'expired' && 'Bu şifre sıfırlama linkinin süresi dolmuş. Lütfen yeni bir şifre sıfırlama talebinde bulunun.'}
              {tokenStatus === 'used' && 'Bu link daha önce kullanılmış. Eğer şifrenizi değiştiremediyseniz, yeni bir şifre sıfırlama talebinde bulunun.'}
              {(tokenStatus === 'invalid' || tokenStatus === 'not_found') && 'Bu şifre sıfırlama linki geçersiz veya hatalı. Lütfen SMS\'inizdeki linkin tamamını kopyaladığınızdan emin olun. Link eksik veya yanlış girilmiş olabilir.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-[#7a2c2c] hover:bg-[#5a1f1f] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#7a2c2c] focus:ring-offset-2 shadow-md hover:shadow-lg"
            >
              Giriş Sayfasına Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center mb-8">
              <img src="/logo.png" alt="Veribil Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Şifreniz Değiştirildi</h3>
            <p className="text-sm text-gray-600 mb-6">
              Şifreniz başarıyla değiştirildi. Giriş sayfasına yönlendiriliyorsunuz...
            </p>
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Veribil Logo" className="h-24 w-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Yeni Şifre Belirleyin</h2>
          <p className="text-sm text-gray-600">
            Hesabınız için yeni bir şifre oluşturun
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-fade-in">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Reset password form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* New password field */}
            <div>
              <label htmlFor="newPassword" className="form-label">
                Yeni Şifre
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  className="form-input pr-10 placeholder-gray-500"
                  placeholder="En az 6 karakter"
                  value={formData.newPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  disabled={isLoading}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm password field */}
            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Yeni Şifre (Tekrar)
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  className="form-input pr-10 placeholder-gray-500"
                  placeholder="Şifrenizi tekrar girin"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative px-4 py-3 text-base font-medium bg-[#7a2c2c] hover:bg-[#5a1f1f] text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#7a2c2c] focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Şifre değiştiriliyor...
                </div>
              ) : (
                'Şifreyi Değiştir'
              )}
            </button>
          </div>

          {/* Back to login link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-[#7a2c2c] hover:text-[#5a1f1f] font-medium"
              disabled={isLoading}
            >
              Giriş sayfasına dön
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
